import { apiSlice } from './apiSlice';

export interface TicketingCursorPage<T> {
  data: T[];
  pagination: { cursor: string | null; hasMore: boolean };
}

export interface TicketingCursorQuery {
  cursor?: string;
  limit?: number;
}

export interface ActivityRead {
  id: string;
  orgId: string;
  kind: 'cinema' | 'concert' | 'theater' | 'sports' | 'class' | 'workshop' | 'reservation';
  name: string;
  status: string;
}

export interface Activity extends ActivityRead {
  config: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ActivityPageRow extends ActivityRead {
  createdAt?: string;
  updatedAt?: string;
}

export interface MovieRead {
  id: string;
  orgId: string;
  title: string;
  status: string;
}

export interface Movie extends MovieRead {
  originalTitle: string | null;
  durationMinutes: number | null;
  rating: string | null;
  attributes: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface MoviePageRow extends MovieRead {
  originalTitle: string | null;
  durationMinutes: number | null;
  rating: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface TicketingRoom {
  id: string;
  orgId: string;
  workspaceId: string;
  name: string;
  seatMapId: string | null;
  capacity: number | null;
  status: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SeatLayoutEntry {
  col: number;
  label: string;
  kind?: 'standard' | 'vip' | 'accessible' | 'loveseat';
}

export interface SeatMapLayoutRow {
  row: number;
  seats: SeatLayoutEntry[];
  gaps: number[];
  labels: string[];
}

export interface SeatMapLayout {
  rows: SeatMapLayoutRow[];
}

export interface SeatMapListRow {
  id: string;
  orgId: string;
  name: string;
}

export interface SeatMap {
  id: string;
  orgId: string;
  name: string;
  layout: SeatMapLayout;
}

export interface Seat {
  id: string;
  seatMapId: string;
  label: string;
  row: number;
  col: number;
  kind?: string | null;
}

export type ShowtimeStatus = 'draft' | 'scheduled' | 'cancelled' | 'completed';

export interface Showtime {
  id: string;
  orgId: string;
  workspaceId: string;
  activityId: string;
  movieId: string | null;
  roomId: string | null;
  startsAt: string;
  endsAt: string | null;
  capacity: number | null;
  status: ShowtimeStatus;
  createdAt?: string;
  updatedAt?: string;
}

export interface ShowtimeFilters extends TicketingCursorQuery {
  status?: ShowtimeStatus;
  activityId?: string;
  movieId?: string;
  roomId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface ActivityBody {
  kind: Activity['kind'];
  name: string;
  config?: Record<string, unknown> | null;
  status?: string;
}

export interface MovieBody {
  title: string;
  originalTitle?: string | null;
  durationMinutes?: number | null;
  rating?: string | null;
  attributes?: Record<string, unknown> | null;
  status?: string;
}

export interface RoomBody {
  name: string;
  seatMapId?: string | null;
  capacity?: number | null;
  status?: string;
}

export interface SeatMapBody {
  name: string;
  layout: SeatMapLayout;
}

export interface SeatBody {
  label: string;
  row: number;
  col: number;
  kind?: SeatLayoutEntry['kind'];
}

export interface ShowtimeBody {
  activityId: string;
  movieId?: string | null;
  roomId?: string | null;
  startsAt: string;
  endsAt?: string | null;
  holdTtlSeconds?: number;
  maxSeatsPerHold?: number;
  capacity?: number | null;
  priceOverrides?: Record<string, string> | null;
}

export interface ShowtimeUpdateBody extends Partial<Omit<ShowtimeBody, 'activityId'>> {
  status?: ShowtimeStatus;
}

export interface TicketingDeleteAcknowledgement {
  id: string;
}

function pageParams(params: TicketingCursorQuery) {
  return { ...(params.cursor ? { cursor: params.cursor } : {}), limit: params.limit ?? 20 };
}

export const ticketingApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getActivitiesPage: builder.query<TicketingCursorPage<ActivityPageRow>, TicketingCursorQuery>({
      query: (params) => ({ url: '/ticketing/v1/ticketing/activities/page', params: pageParams(params) }),
      providesTags: (page) => page
        ? [...page.data.map(({ id }) => ({ type: 'Ticketing' as const, id: `activity-${id}` })), { type: 'Ticketing', id: 'activities' }]
        : [{ type: 'Ticketing', id: 'activities' }],
    }),
    getActivity: builder.query<ActivityRead, string>({
      query: (id) => `/ticketing/v1/ticketing/activities/${encodeURIComponent(id)}`,
      providesTags: (_result, _error, id) => [{ type: 'Ticketing', id: `activity-${id}` }],
    }),
    getActivityForAdmin: builder.query<Activity, string>({
      query: (id) => `/ticketing/v1/ticketing/activities/${encodeURIComponent(id)}/admin`,
      providesTags: (_result, _error, id) => [{ type: 'Ticketing', id: `activity-${id}` }],
    }),
    createActivity: builder.mutation<ActivityRead, ActivityBody>({
      query: (body) => ({ url: '/ticketing/v1/ticketing/activities', method: 'POST', body }),
      invalidatesTags: [{ type: 'Ticketing', id: 'activities' }],
    }),
    updateActivity: builder.mutation<Activity, { id: string; body: Partial<ActivityBody> }>({
      query: ({ id, body }) => ({ url: `/ticketing/v1/ticketing/activities/${encodeURIComponent(id)}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Ticketing', id: `activity-${id}` }, { type: 'Ticketing', id: 'activities' }],
    }),
    deleteActivity: builder.mutation<TicketingDeleteAcknowledgement, string>({
      query: (id) => ({ url: `/ticketing/v1/ticketing/activities/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Ticketing', id: `activity-${id}` }, { type: 'Ticketing', id: 'activities' }],
    }),
    getMoviesPage: builder.query<TicketingCursorPage<MoviePageRow>, TicketingCursorQuery>({
      query: (params) => ({ url: '/ticketing/v1/ticketing/movies/page', params: pageParams(params) }),
      providesTags: (page) => page
        ? [...page.data.map(({ id }) => ({ type: 'Ticketing' as const, id: `movie-${id}` })), { type: 'Ticketing', id: 'movies' }]
        : [{ type: 'Ticketing', id: 'movies' }],
    }),
    getMovie: builder.query<MovieRead, string>({
      query: (id) => `/ticketing/v1/ticketing/movies/${encodeURIComponent(id)}`,
      providesTags: (_result, _error, id) => [{ type: 'Ticketing', id: `movie-${id}` }],
    }),
    getMovieForAdmin: builder.query<Movie, string>({
      query: (id) => `/ticketing/v1/ticketing/movies/${encodeURIComponent(id)}/admin`,
      providesTags: (_result, _error, id) => [{ type: 'Ticketing', id: `movie-${id}` }],
    }),
    createMovie: builder.mutation<MovieRead, MovieBody>({
      query: (body) => ({ url: '/ticketing/v1/ticketing/movies', method: 'POST', body }),
      invalidatesTags: [{ type: 'Ticketing', id: 'movies' }],
    }),
    updateMovie: builder.mutation<Movie, { id: string; body: Partial<MovieBody> }>({
      query: ({ id, body }) => ({ url: `/ticketing/v1/ticketing/movies/${encodeURIComponent(id)}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Ticketing', id: `movie-${id}` }, { type: 'Ticketing', id: 'movies' }],
    }),
    deleteMovie: builder.mutation<TicketingDeleteAcknowledgement, string>({
      query: (id) => ({ url: `/ticketing/v1/ticketing/movies/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Ticketing', id: `movie-${id}` }, { type: 'Ticketing', id: 'movies' }],
    }),
    getRoomsPage: builder.query<TicketingCursorPage<TicketingRoom>, TicketingCursorQuery>({
      query: (params) => ({ url: '/ticketing/v1/ticketing/rooms/page', params: pageParams(params) }),
      providesTags: (page) => page
        ? [...page.data.map(({ id }) => ({ type: 'Ticketing' as const, id: `room-${id}` })), { type: 'Ticketing', id: 'rooms' }]
        : [{ type: 'Ticketing', id: 'rooms' }],
    }),
    getRoom: builder.query<TicketingRoom, string>({
      query: (id) => `/ticketing/v1/ticketing/rooms/${encodeURIComponent(id)}`,
      providesTags: (_result, _error, id) => [{ type: 'Ticketing', id: `room-${id}` }],
    }),
    createRoom: builder.mutation<TicketingRoom, RoomBody>({
      query: (body) => ({ url: '/ticketing/v1/ticketing/rooms', method: 'POST', body }),
      invalidatesTags: [{ type: 'Ticketing', id: 'rooms' }],
    }),
    updateRoom: builder.mutation<TicketingRoom, { id: string; body: Partial<RoomBody> }>({
      query: ({ id, body }) => ({ url: `/ticketing/v1/ticketing/rooms/${encodeURIComponent(id)}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Ticketing', id: `room-${id}` }, { type: 'Ticketing', id: 'rooms' }],
    }),
    deleteRoom: builder.mutation<TicketingDeleteAcknowledgement, string>({
      query: (id) => ({ url: `/ticketing/v1/ticketing/rooms/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Ticketing', id: `room-${id}` }, { type: 'Ticketing', id: 'rooms' }],
    }),
    getSeatMapsPage: builder.query<TicketingCursorPage<SeatMapListRow>, TicketingCursorQuery>({
      query: (params) => ({ url: '/ticketing/v1/ticketing/seat-maps/page', params: pageParams(params) }),
      providesTags: (page) => page
        ? [...page.data.map(({ id }) => ({ type: 'Ticketing' as const, id: `seat-map-${id}` })), { type: 'Ticketing', id: 'seat-maps' }]
        : [{ type: 'Ticketing', id: 'seat-maps' }],
    }),
    getSeatMap: builder.query<SeatMap, string>({
      query: (id) => `/ticketing/v1/ticketing/seat-maps/${encodeURIComponent(id)}`,
      providesTags: (_result, _error, id) => [{ type: 'Ticketing', id: `seat-map-${id}` }],
    }),
    createSeatMap: builder.mutation<SeatMap, SeatMapBody>({
      query: (body) => ({ url: '/ticketing/v1/ticketing/seat-maps', method: 'POST', body }),
      invalidatesTags: [{ type: 'Ticketing', id: 'seat-maps' }],
    }),
    updateSeatMap: builder.mutation<SeatMap, { id: string; body: Partial<SeatMapBody> }>({
      query: ({ id, body }) => ({ url: `/ticketing/v1/ticketing/seat-maps/${encodeURIComponent(id)}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Ticketing', id: `seat-map-${id}` }, { type: 'Ticketing', id: 'seat-maps' }, { type: 'Ticketing', id: `seats-${id}` }],
    }),
    deleteSeatMap: builder.mutation<TicketingDeleteAcknowledgement, string>({
      query: (id) => ({ url: `/ticketing/v1/ticketing/seat-maps/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Ticketing', id: `seat-map-${id}` }, { type: 'Ticketing', id: 'seat-maps' }],
    }),
    getSeatsPage: builder.query<TicketingCursorPage<Seat>, { seatMapId: string } & TicketingCursorQuery>({
      query: ({ seatMapId, ...params }) => ({ url: `/ticketing/v1/ticketing/seat-maps/${encodeURIComponent(seatMapId)}/seats/page`, params: pageParams(params) }),
      providesTags: (page, _error, { seatMapId }) => page
        ? [...page.data.map(({ id }) => ({ type: 'Ticketing' as const, id: `seat-${id}` })), { type: 'Ticketing', id: `seats-${seatMapId}` }]
        : [{ type: 'Ticketing', id: `seats-${seatMapId}` }],
    }),
    getSeat: builder.query<Seat, { seatMapId: string; seatId: string }>({
      query: ({ seatMapId, seatId }) => `/ticketing/v1/ticketing/seat-maps/${encodeURIComponent(seatMapId)}/seats/${encodeURIComponent(seatId)}`,
      providesTags: (_result, _error, { seatId }) => [{ type: 'Ticketing', id: `seat-${seatId}` }],
    }),
    createSeat: builder.mutation<Seat, { seatMapId: string; body: SeatBody }>({
      query: ({ seatMapId, body }) => ({ url: `/ticketing/v1/ticketing/seat-maps/${encodeURIComponent(seatMapId)}/seats`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { seatMapId }) => [{ type: 'Ticketing', id: `seats-${seatMapId}` }],
    }),
    updateSeat: builder.mutation<Seat, { seatMapId: string; seatId: string; body: Partial<SeatBody> }>({
      query: ({ seatMapId, seatId, body }) => ({ url: `/ticketing/v1/ticketing/seat-maps/${encodeURIComponent(seatMapId)}/seats/${encodeURIComponent(seatId)}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { seatMapId, seatId }) => [{ type: 'Ticketing', id: `seat-${seatId}` }, { type: 'Ticketing', id: `seats-${seatMapId}` }],
    }),
    deleteSeat: builder.mutation<TicketingDeleteAcknowledgement, { seatMapId: string; seatId: string }>({
      query: ({ seatMapId, seatId }) => ({ url: `/ticketing/v1/ticketing/seat-maps/${encodeURIComponent(seatMapId)}/seats/${encodeURIComponent(seatId)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { seatMapId, seatId }) => [{ type: 'Ticketing', id: `seat-${seatId}` }, { type: 'Ticketing', id: `seats-${seatMapId}` }],
    }),
    getShowtimesPage: builder.query<TicketingCursorPage<Showtime>, ShowtimeFilters>({
      query: ({ cursor, limit, status, activityId, movieId, roomId, dateFrom, dateTo }) => ({
        url: '/ticketing/v1/ticketing/showtimes/page',
        params: {
          ...pageParams({ cursor, limit }),
          ...(status ? { status } : {}),
          ...(activityId ? { activityId } : {}),
          ...(movieId ? { movieId } : {}),
          ...(roomId ? { roomId } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
        },
      }),
      providesTags: (page) => page
        ? [...page.data.map(({ id }) => ({ type: 'Ticketing' as const, id: `showtime-${id}` })), { type: 'Ticketing', id: 'showtimes' }]
        : [{ type: 'Ticketing', id: 'showtimes' }],
    }),
    getShowtime: builder.query<Showtime, string>({
      query: (id) => `/ticketing/v1/ticketing/showtimes/${encodeURIComponent(id)}`,
      providesTags: (_result, _error, id) => [{ type: 'Ticketing', id: `showtime-${id}` }],
    }),
    createShowtime: builder.mutation<Showtime, ShowtimeBody>({
      query: (body) => ({ url: '/ticketing/v1/ticketing/showtimes', method: 'POST', body }),
      invalidatesTags: [{ type: 'Ticketing', id: 'showtimes' }],
    }),
    updateShowtime: builder.mutation<Showtime, { id: string; body: ShowtimeUpdateBody }>({
      query: ({ id, body }) => ({ url: `/ticketing/v1/ticketing/showtimes/${encodeURIComponent(id)}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Ticketing', id: `showtime-${id}` }, { type: 'Ticketing', id: 'showtimes' }],
    }),
    deleteShowtime: builder.mutation<TicketingDeleteAcknowledgement, string>({
      query: (id) => ({ url: `/ticketing/v1/ticketing/showtimes/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Ticketing', id: `showtime-${id}` }, { type: 'Ticketing', id: 'showtimes' }],
    }),
  }),
});

export const {
  useGetActivitiesPageQuery, useGetActivityQuery, useGetActivityForAdminQuery, useCreateActivityMutation, useUpdateActivityMutation, useDeleteActivityMutation,
  useGetMoviesPageQuery, useGetMovieQuery, useGetMovieForAdminQuery, useCreateMovieMutation, useUpdateMovieMutation, useDeleteMovieMutation,
  useGetRoomsPageQuery, useGetRoomQuery, useCreateRoomMutation, useUpdateRoomMutation, useDeleteRoomMutation,
  useGetSeatMapsPageQuery, useGetSeatMapQuery, useCreateSeatMapMutation, useUpdateSeatMapMutation, useDeleteSeatMapMutation,
  useGetSeatsPageQuery, useGetSeatQuery, useCreateSeatMutation, useUpdateSeatMutation, useDeleteSeatMutation,
  useGetShowtimesPageQuery, useGetShowtimeQuery, useCreateShowtimeMutation, useUpdateShowtimeMutation, useDeleteShowtimeMutation,
} = ticketingApi;
